// EditarHoraDialog — edición de un registro ya capturado.
//
// Reusa los campos del form de captura (SKU combobox, cantidad/operarios/
// golpes/OP) con el gating de golpes_min_aplica. La identidad temporal
// (bloque/línea/fecha) se muestra read-only en el header — es inmutable.
//
// `requiereRazon`:
//   - El líder lo usa SIEMPRE en true: solo puede editar su bloque mientras el
//     turno está activo, con motivo ≥20 chars (vuelve el bloque a pendiente).
//   - El supervisor también en true: motivo ≥20 chars obligatorio.
//
// El SERVER re-valida la autorización; un 423 (fuera de turno) / 409 se
// traduce a un toast claro (manejado acá vía onError).

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { EditarRegistro, RegistroProduccionHora } from 'schemas';
import { Button } from '../../components/ui/button';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api/client';
import { useEditarRegistro, useSkusByLinea } from '../../lib/api/queries';
import { CantidadInput } from './shared';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { limpiarEntradaLote, propsInputLote, validarLoteUi } from '../../lib/formatoLote';

interface EditarHoraDialogProps {
  registro: RegistroProduccionHora | null;
  /** Si true, el campo motivo se muestra y es obligatorio (≥20). */
  requiereRazon: boolean;
  /** Texto contextual bajo el header (ej. "Editás dentro de la ventana libre"). */
  hint?: string;
  onClose: () => void;
  /** Callback opcional al guardar OK (ej. cerrar también un menú padre). */
  onSaved?: () => void;
}

export function EditarHoraDialog({
  registro,
  requiereRazon,
  hint,
  onClose,
  onSaved,
}: EditarHoraDialogProps) {
  const open = registro !== null;
  const editarMut = useEditarRegistro();
  const skusQ = useSkusByLinea(registro?.linea_id ?? null);
  const skus = useMemo(() => skusQ.data ?? [], [skusQ.data]);

  const [skuId, setSkuId] = useState('');
  const [cantidad, setCantidad] = useState<number | null>(null);
  const [operarios, setOperarios] = useState<number | null>(null);
  const [golpes, setGolpes] = useState<number | null>(null);
  const [op, setOp] = useState('');
  const { formatoLote } = useTenantConfig();
  const inputLote = propsInputLote(formatoLote);
  const [razon, setRazon] = useState('');

  // Pre-fill al abrir / cambiar de registro.
  useEffect(() => {
    if (registro) {
      setSkuId(registro.sku_id);
      setCantidad(registro.cantidad_procesada);
      setOperarios(registro.numero_operarios);
      setGolpes(registro.golpes_por_minuto);
      setOp(registro.op_lote_oc_cliente ?? '');
      setRazon('');
    }
  }, [registro]);

  const selectedSku = skus.find((s) => s.id === skuId) ?? null;
  const golpesNoAplica = selectedSku ? !selectedSku.golpes_min_aplica : false;

  // Si el SKU activo no usa golpes/min, forzar null (evita valor fantasma).
  useEffect(() => {
    if (selectedSku && !selectedSku.golpes_min_aplica) setGolpes(null);
  }, [selectedSku]);

  if (!registro) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogHeader>
          <DialogTitle>Editar registro</DialogTitle>
        </DialogHeader>
        <DialogContent />
      </Dialog>
    );
  }

  const horaIni = String(registro.hora_inicio_bloque).slice(0, 5);
  const horaFin = String(registro.hora_fin_bloque).slice(0, 5);

  async function handleGuardar() {
    if (!registro) return;
    // 0 es válido (paro total): el backend verifica que los paros guardados
    // del registro cubran el bloque completo (400 si no) y fuerza golpes null.
    if (cantidad === null || cantidad < 0) {
      toast.error('Cantidad procesada inválida');
      return;
    }
    if (operarios === null || operarios < 1 || operarios > 20) {
      toast.error('Operarios debe estar entre 1 y 20');
      return;
    }
    if (cantidad !== 0 && selectedSku?.golpes_min_aplica && (golpes === null || golpes <= 0)) {
      toast.error('Golpes/min es obligatorio para este SKU');
      return;
    }
    if (op.trim().length === 0) {
      toast.error('OP / Lote es obligatorio');
      return;
    }
    // Formato de la empresa (B7): solo si cambió — un lote ya guardado con un
    // formato anterior no bloquea editar otros campos (el API tampoco lo revalida).
    if (op.trim() !== (registro.op_lote_oc_cliente ?? '')) {
      const loteOk = validarLoteUi(op, formatoLote);
      if (loteOk !== true) {
        toast.error(loteOk);
        return;
      }
    }
    if (requiereRazon && razon.trim().length < 20) {
      toast.error('El motivo debe tener al menos 20 caracteres');
      return;
    }

    // Solo enviar los campos que cambiaron (PATCH parcial). razon va solo si
    // se requiere.
    const cambios: EditarRegistro = {};
    if (skuId !== registro.sku_id) cambios.sku_id = skuId;
    if (cantidad !== registro.cantidad_procesada) cambios.cantidad_procesada = cantidad;
    if (operarios !== registro.numero_operarios) cambios.numero_operarios = operarios;
    if (golpes !== registro.golpes_por_minuto) cambios.golpes_por_minuto = golpes;
    if (op.trim() !== (registro.op_lote_oc_cliente ?? '')) cambios.op_lote_oc_cliente = op.trim();

    const hayCambios = Object.keys(cambios).length > 0;
    if (!hayCambios) {
      toast.error('No modificaste ningún campo');
      return;
    }
    if (requiereRazon) cambios.razon = razon.trim();

    try {
      await editarMut.mutateAsync({ registroId: registro.id, cambios });
      toast.success('Registro actualizado');
      onClose();
      onSaved?.();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 423) {
          toast.error('Fuera del turno; pídale al supervisor que edite este bloque.', {
            description: err.message,
          });
        } else if (err.status === 409) {
          toast.error('Conflicto al editar', { description: err.message });
        } else if (err.status === 403) {
          toast.error('No tiene permiso para editar este registro', { description: err.message });
        } else {
          toast.error('Error al editar', { description: err.message });
        }
      } else {
        toast.error('Error al editar', {
          description: err instanceof Error ? err.message : 'unknown',
        });
      }
    }
  }

  const skuOpts: ComboboxOption[] = skus.map((s) => ({
    value: s.id,
    label: s.codigo_cliente,
    description: `${s.descripcion}${s.formato ? ` · ${s.formato}` : ''}`,
    searchText: `${s.codigo_cliente} ${s.descripcion} ${s.formato ?? ''}`,
  }));

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>
          Editar bloque {horaIni}–{horaFin}
        </DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <div className="rounded bg-surface-overlay p-2 text-xs text-fg-secondary">
          Fecha <strong>{registro.fecha}</strong> · bloque {horaIni}–{horaFin}
          <br />
          {hint ?? 'La fecha, el turno y la hora del bloque no se pueden cambiar.'}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="edit-sku">SKU</Label>
          {skusQ.isLoading ? (
            <p className="text-xs text-fg-tertiary">Cargando SKUs…</p>
          ) : (
            <Combobox
              id="edit-sku"
              options={skuOpts}
              value={skuId}
              onChange={setSkuId}
              placeholder="Buscar por código o descripción…"
              emptyLabel="Sin coincidencias"
            />
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CantidadInput
            id="edit-cantidad"
            label="Cantidad procesada"
            placeholder="Ej. 1.250"
            value={cantidad}
            onChange={setCantidad}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-operarios">Operarios (1–20)</Label>
            <Input
              id="edit-operarios"
              type="number"
              inputMode="numeric"
              min={1}
              max={20}
              step={1}
              placeholder="Ej. 3"
              value={operarios ?? ''}
              onChange={(e) => setOperarios(e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="edit-golpes">
            Golpes/min{golpesNoAplica ? ' (no aplica a este SKU)' : ''}
          </Label>
          <Input
            id="edit-golpes"
            type="number"
            inputMode="decimal"
            min={1}
            step={0.1}
            disabled={golpesNoAplica}
            placeholder={golpesNoAplica ? 'No aplica' : 'Ej. 80'}
            value={golpes ?? ''}
            onChange={(e) => setGolpes(e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="edit-op">OP / Lote / OC Cliente</Label>
          <Input
            id="edit-op"
            type="text"
            inputMode={inputLote.inputMode}
            maxLength={inputLote.maxLength}
            placeholder={inputLote.placeholder}
            value={op}
            onChange={(e) => setOp(limpiarEntradaLote(e.target.value, formatoLote))}
          />
        </div>

        {requiereRazon ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-razon">Motivo de la edición (mínimo 20 caracteres)</Label>
            <Textarea
              id="edit-razon"
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder="Ej: corrección de cantidad por conteo físico final del bloque"
            />
            <p className={`text-xs ${razon.trim().length >= 20 ? 'text-ok' : 'text-fg-tertiary'}`}>
              {razon.trim().length}/20 caracteres mínimos
            </p>
          </div>
        ) : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={editarMut.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleGuardar}
          disabled={editarMut.isPending || (requiereRazon && razon.trim().length < 20)}
        >
          {editarMut.isPending ? (
            <>
              <Spinner className="border-white/30 border-t-white" /> Guardando…
            </>
          ) : (
            'Guardar cambios'
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
